import { money } from '../../format';
import { BALLS, ballsBack, FEVER_PAYS, TULIP_PAYS, type Batch, type PachinkoState } from '../../webGames/pachinko';
import { Burst, Controls, secondChanceFrame, Stage, StatusLine, useAnimClock, useBurst, useLuckMoment, type TableProps } from './Frame';

/** Timing (ms). Balls leave the launcher LAUNCH_MS apart, ride the rail up and
 *  over, then fall through the pins. */
const LAUNCH_MS = 120;
const RAIL_MS = 520;
const FALL_MS = 900;
/** A START spin: reels stop left to right; a reach (two alike) holds the last. */
const SPIN_MS = 1000;
const REACH_MS = 1700;
/** FEVER: the gate opens and the payout rains into the tray over this long. */
const RAIN_MS = 3400;
const END_HOLD_MS = 400;
/** How long each rain ball takes to spill out of the gate and drop away. */
const RAIN_LIFE_MS = 520;
/** A ★ second chance: the lost batch, then luck, then the real batch. */
const LUCK_GAP_MS = 1300;

/** Where each kind of ball ends up on the 300 × 380 playfield. */
const START = { x: 150, y: 178 };
const TULIPS = [{ x: 72, y: 252 }, { x: 228, y: 252 }];
const LCD = { x: 92, y: 66, w: 116, h: 78 };

function seeded(seed: number) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A ball's path: up the rail on the right, over the top, then down through
 *  the pins, zig-zagging to where it lands. */
function pathFor(i: number, end: Batch['balls'][number], salt: number): { x: number; y: number }[] {
  const r = seeded(i * 7919 + salt * 104729 + 17);
  const target = end === 'start' ? START : end === 'tulip' ? TULIPS[i % 2]! : { x: 60 + r() * 180, y: 392 };
  const pts = [
    { x: 284, y: 372 },
    { x: 286, y: 140 },
    { x: 270, y: 34 },
    { x: 210, y: 16 },
    { x: 120 + r() * 60, y: 22 },
  ];
  let x = pts[pts.length - 1]!.x;
  for (let y = 48; y < target.y - 8; y += 26) {
    // Drift toward the target, knocked about by the pins.
    x += (target.x - x) * 0.35 + (r() - 0.5) * 34;
    x = Math.max(24, Math.min(276, x));
    // Round the LCD instead of passing through it.
    if (y > LCD.y - 6 && y < LCD.y + LCD.h + 4 && x > LCD.x - 6 && x < LCD.x + LCD.w + 6) x = x < 150 ? LCD.x - 10 : LCD.x + LCD.w + 10;
    pts.push({ x, y });
  }
  pts.push(target);
  return pts;
}

function along(pts: { x: number; y: number }[], f: number) {
  const segs = pts.length - 1;
  const at = Math.max(0, Math.min(segs, f * segs));
  const k = Math.min(segs - 1, Math.floor(at));
  const u = at - k;
  return { x: pts[k]!.x + (pts[k + 1]!.x - pts[k]!.x) * u, y: pts[k]!.y + (pts[k + 1]!.y - pts[k]!.y) * u };
}

interface Timeline {
  /** When each ball lands (ms from the batch's start). */
  lands: number[];
  /** Each START spin: when it starts, when its reels stop, and if it's a reach. */
  spins: { start: number; stops: [number, number, number]; reach: boolean; fever: boolean; reels: [number, number, number] }[];
  end: number;
}

function timeline(b: Batch): Timeline {
  const lands = b.balls.map((_, i) => i * LAUNCH_MS + RAIL_MS + FALL_MS);
  const spins: Timeline['spins'] = [];
  let free = 0;
  let k = 0;
  b.balls.forEach((end, i) => {
    if (end !== 'start') return;
    const sp = b.spins[k++]!;
    const start = Math.max(lands[i]!, free);
    const reach = sp.reels[0] === sp.reels[1];
    const stop3 = start + (reach ? REACH_MS : SPIN_MS);
    spins.push({ start, stops: [start + 450, start + 700, stop3], reach, fever: sp.fever, reels: sp.reels });
    free = stop3 + (sp.fever ? RAIN_MS + 300 : 200);
  });
  const end = Math.max(lands.at(-1)! + 200, free) + END_HOLD_MS;
  return { lands, spins, end };
}

/** Balls back so far at time t (tulips and START as they land; FEVER when its spin ends). */
function paidSoFar(b: Batch, tl: Timeline, t: number): number {
  let n = 0;
  b.balls.forEach((end, i) => {
    if (t >= tl.lands[i]!) n += end === 'tulip' ? TULIP_PAYS : end === 'start' ? 1 : 0;
  });
  // A FEVER pays ball by ball as the rain falls.
  for (const s of tl.spins) if (s.fever && t >= s.stops[2]) n += Math.min(FEVER_PAYS, Math.floor(((t - s.stops[2]) / RAIN_MS) * FEVER_PAYS));
  return n;
}

function Playfield({ batch, t, salt }: { batch: Batch | null; t: number; salt: number }) {
  const tl = batch ? timeline(batch) : null;
  const spin = tl ? [...tl.spins].reverse().find((s) => t >= s.start) : undefined;
  const fevering = !!spin && spin.fever && t >= spin.stops[2] && t < spin.stops[2] + RAIN_MS;
  const reel = (k: number) => {
    if (!spin) return 7;
    if (t >= spin.stops[k]!) return spin.reels[k]!;
    return (Math.floor(t / 70) + k * 3) % 9 + 1;
  };
  const reachNow = !!spin && spin.reach && t >= spin.stops[1] && t < spin.stops[2];
  const litTulip = (side: number) => !!batch && batch.balls.some((e, i) => e === 'tulip' && i % 2 === side && tl && t >= tl.lands[i]! && t < tl.lands[i]! + 500);
  const startLit = !!batch && batch.balls.some((e, i) => e === 'start' && tl && t >= tl.lands[i]! && t < tl.lands[i]! + 500);

  // The pins: staggered rows, skipping the LCD and the pockets.
  const pins: { x: number; y: number }[] = [];
  for (let row = 0; row < 13; row++) {
    const y = 40 + row * 24;
    for (let x = 30 + (row % 2) * 12; x < 262; x += 24) {
      const inLcd = x > LCD.x - 8 && x < LCD.x + LCD.w + 8 && y > LCD.y - 8 && y < LCD.y + LCD.h + 8;
      const near = [START, ...TULIPS].some((p) => Math.abs(p.x - x) < 16 && Math.abs(p.y - y) < 14);
      const gate = y > 300 && x > 110 && x < 190;
      if (!inLcd && !near && !gate) pins.push({ x, y });
    }
  }

  const balls = batch && tl ? batch.balls.map((end, i) => {
    const t0 = i * LAUNCH_MS;
    if (t < t0 || t > tl.lands[i]! + 120) return null;
    const f = (t - t0) / (RAIL_MS + FALL_MS);
    return { ...along(pathFor(i, end, salt), f), i };
  }).filter(Boolean) as { x: number; y: number; i: number }[] : [];

  const rain: { x: number; y: number; k: number }[] = [];
  if (tl) {
    for (const s of tl.spins) {
      if (!s.fever || t < s.stops[2] || t > s.stops[2] + RAIN_MS + RAIN_LIFE_MS) continue;
      const since = t - s.stops[2];
      for (let j = 0; j < FEVER_PAYS; j++) {
        const born = (j / FEVER_PAYS) * RAIN_MS;
        const age = since - born;
        if (age < 0 || age > RAIN_LIFE_MS) continue;
        const u = age / RAIN_LIFE_MS;
        const r = seeded(j * 131 + 7);
        const side = (r() - 0.5) * 2;
        rain.push({ x: 150 + side * (10 + u * 70), y: 330 + u * u * 70 - Math.sin(Math.PI * u) * 14, k: s.start * 1000 + j });
      }
    }
  }

  return (
    <svg className={`pachi-field${fevering ? ' fever' : ''}`} viewBox="0 0 300 380" role="img" aria-label="The pachinko playfield">
      <rect x="4" y="4" width="292" height="372" rx="26" className="pachi-glass" />
      <path d="M284 372 L286 140 C286 70 250 24 180 14 C120 8 60 18 30 46" className="pachi-rail" />
      {pins.map((p, k) => <circle key={k} cx={p.x} cy={p.y} r="2.1" className="pachi-pin" />)}
      <g className="pachi-lcd">
        <rect x={LCD.x} y={LCD.y} width={LCD.w} height={LCD.h} rx="8" className="lcd-frame" />
        <rect x={LCD.x + 6} y={LCD.y + 6} width={LCD.w - 12} height={LCD.h - 26} rx="4" className="lcd-screen" />
        {[0, 1, 2].map((k) => (
          <text key={k} x={LCD.x + 26 + k * 32} y={LCD.y + 44} className={`lcd-digit${spin && t < spin.stops[k]! ? ' spinning' : ''}`} textAnchor="middle">{reel(k)}</text>
        ))}
        {/* Between spins the screen says what its numbers mean (user,
            2026-09-27: it used to read "LUCKY 777", as if only sevens won). */}
        <text
          x={150}
          y={LCD.y + LCD.h - 7}
          className={`lcd-word${fevering ? ' fever' : reachNow ? ' reach' : ''}`}
          textAnchor="middle"
          {...(!fevering && !reachNow ? { textLength: LCD.w - 14, lengthAdjust: 'spacingAndGlyphs' } : {})}
        >
          {fevering ? 'FEVER!' : reachNow ? 'REACH!' : '3 ALIKE = FEVER'}
        </text>
      </g>
      <g className={`pachi-pocket start${startLit ? ' lit' : ''}`}>
        <path d={`M${START.x - 14} ${START.y - 8} L${START.x + 14} ${START.y - 8} L${START.x + 9} ${START.y + 8} L${START.x - 9} ${START.y + 8} Z`} />
        <text x={START.x} y={START.y + 4} textAnchor="middle">START</text>
      </g>
      {TULIPS.map((p, side) => (
        <g key={side} className={`pachi-pocket tulip${litTulip(side) ? ' lit' : ''}`}>
          <path d={`M${p.x - 13} ${p.y - 10} Q${p.x - 8} ${p.y + 12} ${p.x} ${p.y + 8} Q${p.x + 8} ${p.y + 12} ${p.x + 13} ${p.y - 10} Q${p.x} ${p.y} ${p.x - 13} ${p.y - 10} Z`} />
        </g>
      ))}
      <g className={`pachi-gate${fevering ? ' open' : ''}`}>
        <rect x="112" y="318" width="76" height="20" rx="4" className="gate-body" />
        <rect x="116" y="322" width="68" height="12" rx="2" className="gate-door" />
      </g>
      <circle cx="150" cy="364" r="9" className="pachi-out" />
      {balls.map((b) => <circle key={b.i} cx={b.x} cy={b.y} r="5" className="pachi-ball" />)}
      {rain.map((b) => <circle key={b.k} cx={b.x} cy={b.y} r="4.4" className="pachi-ball rain" />)}
    </svg>
  );
}

/**
 * What each pocket pays, as lit chips in the cabinet's own neon (user,
 * 2026-09-27: pay info written out as text didn't fit the vibe): the
 * tulip, the START pocket, and three alike on the screen for FEVER.
 */
function PachiPays() {
  return (
    <div className="pachi-pays" aria-label={`Tulip pays ${TULIP_PAYS} balls. START pays 1 ball and spins the screen. Three alike is FEVER, ${FEVER_PAYS} balls.`}>
      <span className="pachi-pay">
        <svg viewBox="-15 -12 30 24" aria-hidden="true" className="pachi-pocket tulip">
          <path d="M-13 -10 Q-8 12 0 8 Q8 12 13 -10 Q0 0 -13 -10 Z" />
        </svg>
        <b>+{TULIP_PAYS}</b>
      </span>
      <span className="pachi-pay">
        <svg viewBox="-15 -10 30 20" aria-hidden="true" className="pachi-pocket start">
          <path d="M-14 -8 L14 -8 L9 8 L-9 8 Z" />
          <text x="0" y="2.5" textAnchor="middle">START</text>
        </svg>
        <b>+1</b>
        <small>&amp; spin</small>
      </span>
      <span className="pachi-pay fever">
        <span className="pachi-pay-lcd" aria-hidden="true">3 alike</span>
        <b>+{FEVER_PAYS}</b>
        <small>fever</small>
      </span>
    </div>
  );
}

export function PachinkoTable({ snapshot, actions }: TableProps) {
  const run = snapshot.run!;
  const state = (run.tables as Record<string, { state: unknown } | undefined>).pachinko?.state as PachinkoState | undefined;
  const anim = snapshot.anim;
  const luck = !!secondChanceFrame(snapshot) && !!state?.lost;
  const lostTl = luck ? timeline(state!.lost!) : null;
  const realTl = state?.batch ? timeline(state.batch) : null;
  const realStart = lostTl ? lostTl.end + LUCK_GAP_MS : 0;
  const elapsed = useAnimClock(snapshot, actions, anim && realTl ? realStart + realTl.end : 0);
  const [burst, fading, setBurst] = useBurst();
  useLuckMoment(snapshot, elapsed, lostTl ? lostTl.end : null, () => ({ what: 'A losing batch is fired again.', saved: !!state?.lucky }), setBurst);

  // What's on the machine right now.
  let batch: Batch | null = state?.batch ?? null;
  let t = Infinity;
  let salt = 1;
  if (anim && state?.batch) {
    if (lostTl && elapsed < realStart) {
      batch = state.lost!;
      t = elapsed;
      salt = 2;
    } else t = elapsed - realStart;
  }
  const tl = batch ? timeline(batch) : null;
  const paid = batch && tl ? paidSoFar(batch, tl, t) : 0;

  let result: string | undefined;
  let tone: 'win' | 'loss' | '' = '';
  if (state?.batch && !anim) {
    const net = state.returned - state.bet;
    const fevers = state.batch.spins.filter((s) => s.fever).length;
    result = `${ballsBack(state.batch)} balls back${fevers ? ` · ${fevers > 1 ? `${fevers}× ` : ''}FEVER` : ''} · ${state.lucky ? '★ ' : ''}${net > 0 ? `+${money(net)}` : net === 0 ? 'even' : `−${money(-net)}`}`;
    tone = net > 0 ? 'win' : net < 0 ? 'loss' : '';
  }

  return (
    <>
      <Stage className="pachi-stage" onClick={anim ? actions.skip : undefined}>
        <Burst burst={burst} fading={fading} pending={false} />
        <div className="pachi-cabinet">
          <div className="pachi-sign" aria-hidden="true">パチンコ <b>PACHINKO</b></div>
          <Playfield batch={batch} t={t} salt={salt} />
          <div className="pachi-tray">
            <span>BALLS <b>{BALLS}</b></span>
            <span>BACK <b className={paid > BALLS ? 'lit' : ''}>{anim || state?.batch ? paid : 0}</b></span>
          </div>
        </div>
        {!state?.batch && <PachiPays />}
        <StatusLine snapshot={snapshot} result={result} tone={tone} />
      </Stage>
      <Controls snapshot={snapshot} actions={actions} onAct={() => setBurst(null)} />
    </>
  );
}
